<div id="application-editor-container" class="content-block flex-left-column light-control-host">
  <form id="scroll-id" [formGroup]="applicationFormModel" (ngSubmit)="saveApplication()">
    <div class="header-container" stickyHeight>
      <div>
        <h1 id="application-settings-header">Application Settings</h1>
      </div>
      <div class="flex-center-row form-controls">
        @if (!saving && permissionsLoaded) {
          <button
            id="cancel-button"
            data-cy="cancel-button"
            type="button"
            class="arpio-secondary-button"
            (click)="cancelClick()"
          >
            @if (canModify) {
              <span>Cancel</span>
            }
            @if (!canModify) {
              <span><svg lucideArrowLeft></svg> Go Back</span>
            }
          </button>
        }
        @if (!saving && !permissionsLoaded) {
          <span>
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          </span>
        }
        @if (!saving && canModify) {
          <button
            data-cy="application-save-button"
            id="save-button"
            class="arpio-button margin-left"
            type="submit"
            [disabled]="!applicationFormModel.valid || !canModify"
          >
            Save
          </button>
        } @else {
          @if (canModify) {
            <span class="editor-spinner-wrap">
              <svg lucideLoaderCircle class="lucide-spin"></svg>
            </span>
          }
        }
      </div>
    </div>
    @if (permissionsLoaded && !canModify) {
      <div class="notice-block">
        You have read-only access to this application. You cannot modify any settings or resources. Contact your
        administrator to request modification permissions.
      </div>
    }
    @if (permissionsLoaded && canModify && !canModify) {
      <div class="notice-block">
        You do not have permission to edit application resources. You need Manager level access or higher.
      </div>
    }

    @if (isInActiveRecovery) {
      <div class="setup-notice warning">
        <div class="notice-icon">
          <svg lucideCircleAlert></svg>
        </div>
        <div class="notice-content">
          <strong>This application is in active recovery</strong>
          <p>Resources you add now won't appear in the recovery environment until the next recovery.</p>
        </div>
      </div>
    }

    @if (isInTestRecovery) {
      <div class="setup-notice warning">
        <div class="notice-icon">
          <svg lucideCircleAlert></svg>
        </div>
        <div class="notice-content">
          <strong>This application is in test recovery</strong>
          <p>Resources you add now won't appear in this test recovery environment.</p>
        </div>
      </div>
    }

    <!-- Tabs Navigation -->
    <app-tabs
      id="application-editor-tabs"
      [tabs]="tabs"
      [useRouting]="false"
      [activeTabIndex]="activeTabIndex"
      (tabChange)="onTabChange($event)"
    ></app-tabs>

    <!-- GENERAL TAB CONTENT -->
    @if (activeTab === 'general') {
      <div class="tab-content" id="general-tab-content" data-cy="general-tab-content">
        @if (
          applicationFormModel.errors?.['existingAppWithSameName'] &&
          (applicationFormModel.touched || applicationFormModel.dirty)
        ) {
          <div class="error margin-top margin-bottom">
            This application cannot be saved because another application exists with the same name.
          </div>
        }
        <!-- Basic Settings Section -->
        <div class="settings-section">
          <h3 class="section-header">Basic Settings</h3>
          <div class="section-content">
            <label class="field-label">Your application name</label>
            <input
              id="application-name-input"
              class="arpio-input full-width"
              type="text"
              formControlName="applicationName"
              placeholder="Application name"
              focus-element
              [disabled]="!canModify"
              [readOnly]="!canModify"
            />
          </div>
        </div>
        <!-- Recovery Point Objective Section -->
        <div class="settings-section">
          <div class="section-header-with-value">
            <h3 class="section-header">Recovery Point Objective</h3>
            <span class="header-value">{{ rpoText }}</span>
          </div>
          <div class="section-content">
            <p class="field-label">The maximum amount of data loss acceptable during a disaster, measured in time.</p>
            <rpo-range-slider
              [enableRealTimeRpos]="enableRealTimeRpo"
              formControlName="applicationRpo"
              [class.pointer-events-none]="!canModify"
              [class.opacity-50]="!canModify"
            ></rpo-range-slider>
            @if (isRealTimeRpo) {
              <about-real-time-replication [applicationType]="applicationType"></about-real-time-replication>
            }
          </div>
        </div>
        <!-- Notifications Section -->
        <div class="settings-section">
          <h3 class="section-header">Notifications</h3>
          <div class="section-content">
            <label class="field-label">Email recipients for alerts and notifications</label>
            <string-list-control
              class="full-width"
              [class.pointer-events-none]="!canModify"
              [class.opacity-50]="!canModify"
              [validationFunction]="isValidEmail"
              formControlName="applicationNotificationEmails"
            ></string-list-control>
          </div>
        </div>
        <!-- Add Ons Section (conditional) -->
        <ng-container *ngLet="accountIntegrations as accountInts">
          @if (accountInts.length > 0) {
            <div class="settings-section">
              <h3 class="section-header">Add Ons</h3>
              <div class="section-content">
                <application-integrations-form-control
                  formControlName="applicationIntegrationIds"
                  [accountIntegrations]="accountInts"
                  [disabled]="!canModify"
                  [readOnly]="!canModify"
                ></application-integrations-form-control>
              </div>
            </div>
          }
        </ng-container>
        <!-- Access Method Section (AWS with StackSets capability only) -->
        @if (showAccessTab) {
          <div id="access-method-section" class="settings-section">
            <div class="section-header-with-value">
              <h3 class="section-header">Access Method</h3>
              @if (!accessMethodLoading) {
                <span class="header-value">{{
                  selectedAccessMethod === AccessMethod.STACKSETS ? 'AWS StackSets' : 'CloudFormation'
                }}</span>
              }
              @if (accessMethodLoading) {
                <span class="header-value">
                  <svg lucideLoaderCircle class="lucide-spin"></svg>
                </span>
              }
            </div>
            <div class="section-content">
              <!-- Loading state -->
              @if (accessMethodLoading) {
                <div class="access-method-loading">
                  <svg lucideLoaderCircle class="lucide-spin"></svg> Loading access configuration...
                </div>
              }
              <!-- Loaded content -->
              @if (!accessMethodLoading) {
                <p class="field-label">Choose how {{ uiService.productName }} connects to your AWS accounts:</p>
                <!-- CloudFormation Status Section (only shown when CloudFormation is selected) -->
                @if (selectedAccessMethod === AccessMethod.CLOUDFORMATION) {
                  <!-- CloudFormation Fully Configured Success Notice -->
                  @if (cloudFormationConfigured) {
                    <div class="setup-notice success">
                      <div class="notice-icon">
                        <svg lucideCircleCheck></svg>
                      </div>
                      <div class="notice-content">
                        <p>CloudFormation Stacks are deployed and providing access to your AWS accounts.</p>
                      </div>
                    </div>
                  }
                  <!-- CloudFormation Access Lost - before user selects a method -->
                  @if (cloudFormationAccessLost && !accessMethodDirty) {
                    <div class="setup-notice warning">
                      <div class="notice-icon">
                        <svg lucideCircleAlert></svg>
                      </div>
                      <div class="notice-content">
                        <strong>Access not detected</strong>
                        <p>
                          Access appears to have been removed. Select an access method below to restore access to your
                          AWS accounts.
                        </p>
                      </div>
                    </div>
                  }
                  <!-- CloudFormation Access Lost - after user confirms CloudFormation -->
                  @if (cloudFormationAccessLost && accessMethodDirty) {
                    <div class="setup-notice warning">
                      <div class="notice-icon">
                        <svg lucideCircleAlert></svg>
                      </div>
                      <div class="notice-content">
                        <strong>Access not detected</strong>
                        <p>Follow steps to deploy CloudFormation templates to grant access.</p>
                      </div>
                      <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                        Configure Access
                      </button>
                    </div>
                  }
                  <!-- CloudFormation Not Configured - currently using StackSets -->
                  @if (cloudFormationNotConfigured) {
                    <div class="stacksets-status-section">
                      <div class="setup-notice warning">
                        <div class="notice-icon">
                          <svg lucideCircleAlert></svg>
                        </div>
                        <div class="notice-content">
                          <p>
                            Arpio detected that you currently have access managed by StackSets. To switch to standalone
                            Stacks, you'll need to deploy CloudFormation Stack templates.
                          </p>
                        </div>
                        <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                          Set Up Access
                        </button>
                      </div>
                      <!-- Per-account status showing StackSet-managed status -->
                      <div class="access-status-cards">
                        <!-- Primary Access Status -->
                        <div class="access-status-card">
                          <div class="status-header">
                            <svg lucideMapPin></svg>
                            <span>Primary Access</span>
                            <code>{{ sourceAccountId }} / {{ sourceRegion }}</code>
                          </div>
                          <div class="status-body">
                            @if (sourceManagedByStackSet) {
                              <div class="status-item info">
                                <svg lucideInfo></svg>
                                <span>Currently managed by StackSets - needs standalone CloudFormation Stack</span>
                              </div>
                            }
                            @if (!sourceManagedByStackSet && sourceAccessService?.stack) {
                              <div class="status-item success">
                                <svg lucideCircleCheck></svg>
                                <span
                                  >CloudFormation Stack <code>{{ sourceAccessService?.stack }}</code> deployed</span
                                >
                              </div>
                            }
                          </div>
                        </div>
                        <!-- Recovery Access Status -->
                        <div class="access-status-card">
                          <div class="status-header">
                            <svg lucideLocateFixed></svg>
                            <span>Recovery Access</span>
                            <code>{{ targetAccountId }} / {{ targetRegion }}</code>
                          </div>
                          <div class="status-body">
                            @if (targetManagedByStackSet) {
                              <div class="status-item info">
                                <svg lucideInfo></svg>
                                <span>Currently managed by StackSets - needs standalone CloudFormation Stack</span>
                              </div>
                            }
                            @if (!targetManagedByStackSet && targetAccessService?.stack) {
                              <div class="status-item success">
                                <svg lucideCircleCheck></svg>
                                <span
                                  >CloudFormation Stack <code>{{ targetAccessService?.stack }}</code> deployed</span
                                >
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                }
                <!-- StackSets Status Section (only shown when StackSets is selected) -->
                @if (selectedAccessMethod === AccessMethod.STACKSETS) {
                  <!-- StackSets Fully Configured Success Notice -->
                  @if (stackSetsConfigured) {
                    <div class="setup-notice success">
                      <div class="notice-icon">
                        <svg lucideCircleCheck></svg>
                      </div>
                      <div class="notice-content">
                        <p>AWS StackSets are deployed and managing access to your AWS accounts.</p>
                      </div>
                    </div>
                  }
                  <!-- StackSets Not Configured - show detailed status -->
                  @if (!stackSetsConfigured) {
                    <div class="stacksets-status-section">
                      <!-- Overall warning -->
                      <div class="setup-notice warning">
                        <div class="notice-icon">
                          <svg lucideCircleAlert></svg>
                        </div>
                        <div class="notice-content">
                          @if (stackSetsNotConfigured) {
                            <p>
                              Before you can save changes here, this application needs AWS StackSets configured. This
                              ensures Arpio maintains access to your recovery environments.
                            </p>
                          }
                          @if (stackSetsPartiallyConfigured) {
                            <p>
                              AWS StackSets are partially configured. One or more accounts still need StackSets
                              deployment.
                            </p>
                          }
                        </div>
                        <button type="button" class="small-button arpio-button" (click)="navigateToUpgradeTab()">
                          Set Up Access
                        </button>
                      </div>
                      <!-- Per-account status -->
                      <div class="access-status-cards">
                        <!-- Primary Access Status -->
                        <div class="access-status-card">
                          <div class="status-header">
                            <svg lucideMapPin></svg>
                            <span>Primary Access</span>
                            <code>{{ sourceAccountId }} / {{ sourceRegion }}</code>
                          </div>
                          <div class="status-body">
                            @if (sourceManagedByStackSet) {
                              <div class="status-item success">
                                <svg lucideCircleCheck></svg>
                                <span>Managed by StackSets</span>
                              </div>
                            }
                            @if (sourceHasCfnButNotStackSet) {
                              <div class="status-item info">
                                <svg lucideInfo></svg>
                                <span
                                  >CloudFormation Stack <code>{{ sourceAccessService?.stack }}</code> exists but is not
                                  managed by StackSets</span
                                >
                              </div>
                            }
                            @if (!sourceManagedByStackSet && !sourceHasCfnButNotStackSet) {
                              <div class="status-item warning">
                                <svg lucideCircleX></svg>
                                <span>StackSets not deployed</span>
                              </div>
                            }
                          </div>
                        </div>
                        <!-- Recovery Access Status -->
                        <div class="access-status-card">
                          <div class="status-header">
                            <svg lucideLocateFixed></svg>
                            <span>Recovery Access</span>
                            <code>{{ targetAccountId }} / {{ targetRegion }}</code>
                          </div>
                          <div class="status-body">
                            @if (targetManagedByStackSet) {
                              <div class="status-item success">
                                <svg lucideCircleCheck></svg>
                                <span>Managed by StackSets</span>
                              </div>
                            }
                            @if (targetHasCfnButNotStackSet) {
                              <div class="status-item info">
                                <svg lucideInfo></svg>
                                <span
                                  >CloudFormation Stack <code>{{ targetAccessService?.stack }}</code> exists but is not
                                  managed by StackSets</span
                                >
                              </div>
                            }
                            @if (!targetManagedByStackSet && !targetHasCfnButNotStackSet) {
                              <div class="status-item warning">
                                <svg lucideCircleX></svg>
                                <span>StackSets not deployed</span>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                }
                <div class="access-method-cards">
                  <!-- CloudFormation Templates Card -->
                  <div
                    class="access-method-card light-content-panel"
                    [class.selected]="
                      selectedAccessMethod === AccessMethod.CLOUDFORMATION &&
                      (!cloudFormationAccessLost || accessMethodDirty)
                    "
                    [class.disabled]="!canModify"
                    (click)="canModify && selectAccessMethod(AccessMethod.CLOUDFORMATION)"
                  >
                    <div class="card-header">
                      <input
                        type="radio"
                        name="selectedAccessMethod"
                        [value]="AccessMethod.CLOUDFORMATION"
                        [checked]="
                          selectedAccessMethod === AccessMethod.CLOUDFORMATION &&
                          (!cloudFormationAccessLost || accessMethodDirty)
                        "
                        [disabled]="!canModify"
                        (change)="selectAccessMethod(AccessMethod.CLOUDFORMATION)"
                      />
                      <h3>CloudFormation Templates</h3>
                    </div>
                    <div class="card-description">
                      Deploy access templates individually to each AWS account. Best for simple setups or accounts
                      outside an AWS Organization.
                    </div>
                    <div class="card-features">
                      <div class="feature"><svg lucideCheck></svg> Works with any AWS account</div>
                      <div class="feature"><svg lucideCheck></svg> No Organization required</div>
                    </div>
                  </div>
                  <!-- AWS StackSets Card -->
                  <div
                    class="access-method-card light-content-panel"
                    [class.selected]="selectedAccessMethod === AccessMethod.STACKSETS"
                    [class.disabled]="!canModify"
                    (click)="canModify && selectAccessMethod(AccessMethod.STACKSETS)"
                  >
                    <div class="card-header">
                      <input
                        type="radio"
                        name="selectedAccessMethod"
                        [value]="AccessMethod.STACKSETS"
                        [checked]="selectedAccessMethod === AccessMethod.STACKSETS"
                        [disabled]="!canModify"
                        (change)="selectAccessMethod(AccessMethod.STACKSETS)"
                      />
                      <h3>AWS StackSets</h3>
                    </div>
                    <div class="card-description">
                      Deploy once from your management account and automatically fan out to member accounts. Best for
                      multi-account Organizations.
                    </div>
                    <div class="card-features">
                      <div class="feature"><svg lucideCheck></svg> Centralized management</div>
                      <div class="feature"><svg lucideCheck></svg> Auto-deploy to new accounts</div>
                    </div>
                  </div>
                </div>
              }
              <!-- End loaded content -->
            </div>
          </div>
        }
        @if (canDelete) {
          <hr />
          <div class="margin-top margin-bottom">
            <p><strong>Need to delete this application from your account?</strong></p>
            <a id="delete-button" data-cy="delete-button" class="underline" [routerLink]="" (click)="deleteClick()">
              Delete application...
            </a>
          </div>
        }
      </div>
    }
    <!-- END GENERAL TAB CONTENT -->

    <!-- RESOURCES TAB CONTENT -->
    @if (activeTab === 'resources') {
      <div class="tab-content" id="resources-tab-content">
        @if (canModify) {
          <p class="double-margin-bottom">
            Configure which resources you want to be included in your Arpio application. Resources selected here will
            failover together.
          </p>
        }
        <!-- App resource selection begins -->
        <div id="application-resource-selection"></div>
        <div class="control-header" id="application-resources-header">
          @if (!canModify) {
            <div>View the resources and rules configured for this application.</div>
          }
        </div>
        <app-resource-selection
          [formGroup]="applicationFormModel"
          [possibleTagValues]="possibleTagValues"
          [possibleResourceTypes]="possibleResourceTypes | async"
          [possibleResourceGroups]="possibleResourceGroups"
          [applicationType]="applicationType"
          [resources]="resources"
          [inclusionRulesCount$]="inclusionRulesCount$"
          [exclusionRulesCount$]="exclusionRulesCount$"
          [includeRulesOpen]="includeRulesOpen"
          [excludeRulesOpen]="excludeRulesOpen"
          [shouldObscureExclusionRules]="shouldObscureExclusionRules"
          [refreshing]="refreshing"
          [filterText]="filterText"
          [rpo]="rpo"
          [showRescanButton]="true"
          [readonly]="!canModify"
          (toggleIncludeRules)="toggleIncludeConditions()"
          (toggleExcludeRules)="toggleExcludeConditions()"
          (toggleExclusionRulesEnabled)="toggleExclusionRules()"
          (filterChange)="doFilterResources($event)"
          (refreshClick)="refreshButtonClick()"
        >
        </app-resource-selection>
      </div>
    }
    <!-- END RESOURCES TAB CONTENT -->

    <!-- UPGRADE TAB CONTENT -->
    @if (activeTab === 'upgrade') {
      <div class="tab-content" id="upgrade-tab-content">
        @if (loadingAccessTemplates) {
          <div class="notice-block"><svg lucideLoaderCircle class="lucide-spin"></svg> Loading access templates...</div>
        }
        @if (!loadingAccessTemplates) {
          <!-- Upgrade Notice with benefits list (hidden during migration to avoid confusion) -->
          @if (!needsAccessConfiguration) {
            <app-application-upgrade-notice
              [upgradeNeeded]="upgradeNeeded"
              [canModify]="canModify"
              [releaseDetails$]="releaseDetails$"
              [upgradeDetails]="upgradeDetails.bind(this)"
              [hasUpgradeDetails]="hasUpgradeDetails.bind(this)"
            ></app-application-upgrade-notice>
          }
          @if (applicationType === 'aws') {
            <!-- CloudFormation method: show individual account access views -->
            @if (selectedAccessMethod === AccessMethod.CLOUDFORMATION) {
              <!-- Access lost: prompt user to select a method before showing setup steps -->
              @if (cloudFormationAccessLost && showAccessTab && !accessMethodDirty) {
                <div class="setup-notice warning margin-top">
                  <div class="notice-icon">
                    <svg lucideCircleAlert></svg>
                  </div>
                  <div class="notice-content">
                    <strong>Access not detected</strong>
                    <p>
                      Access appears to have been removed. Select your preferred access method under
                      <a class="inline-link" (click)="navigateToAccessMethod()">Access Method</a> in General settings.
                    </p>
                  </div>
                </div>
              } @else {
                <div class="upgrade-section">
                  @if (upgradeNeeded) {
                    <h2>Update Access Templates</h2>
                  }
                  @if (!upgradeNeeded && cloudFormationNotConfigured) {
                    <h2>Set Up CloudFormation Access</h2>
                  }
                  @if (upgradeNeeded) {
                    <p class="intro">
                      We've enhanced {{ uiService.productName }}. To get this new functionality, update the
                      CloudFormation Stacks that grant {{ uiService.productName }} access to your AWS environment.
                    </p>
                  }
                  @if (!upgradeNeeded && cloudFormationNotConfigured) {
                    <p class="intro">
                      Deploy CloudFormation templates to grant {{ uiService.productName }} access to your AWS accounts.
                      After deployment, you can optionally remove the StackSets from your management account.
                    </p>
                  }
                </div>
                <aws-account-access-view
                  class="margin-top"
                  [state]="sourceAccessService?.state"
                  [stackName]="sourceAccessService?.stack"
                  [accessType]="AwsAccessScope.SOURCE"
                  [templateUrl]="sourceAccessTemplate"
                  [awsAccountId]="sourceAccountId"
                  [region]="sourceRegion"
                  [completedTestCount]="sourceAccessService?.tests"
                  [managedByStackSet]="sourceManagedByStackSet"
                  [stickyReferenceId]="'application-editor-tabs'"
                  (buttonClicked)="sourceAccessService?.validateAccess()"
                >
                </aws-account-access-view>
                <aws-account-access-view
                  class="margin-top"
                  [state]="targetAccessService?.state"
                  [stackName]="targetAccessService?.stack"
                  [accessType]="AwsAccessScope.TARGET"
                  [templateUrl]="targetAccessTemplate"
                  [awsAccountId]="targetAccountId"
                  [region]="targetRegion"
                  [completedTestCount]="targetAccessService?.tests"
                  [managedByStackSet]="targetManagedByStackSet"
                  [stickyReferenceId]="'application-editor-tabs'"
                  (buttonClicked)="targetAccessService?.validateAccess()"
                >
                </aws-account-access-view>
              }
              <!-- Normal setup steps: shown when access is healthy or user has selected a method -->
            }
            <!-- StackSets method: show StackSets setup or update instructions -->
            @if (selectedAccessMethod === AccessMethod.STACKSETS) {
              <!-- Setup/Migration mode: Use full stacksets-instructions with test access -->
              @if (needsStackSetsSetup) {
                <div class="upgrade-section">
                  <h2>Set Up StackSets Access</h2>
                  <p class="intro">
                    You've chosen to manage {{ uiService.productName }} access with CloudFormation StackSets. Follow
                    these steps to deploy StackSets and grant {{ uiService.productName }} access to your AWS accounts.
                  </p>
                </div>
                <stacksets-instructions
                  class="margin-top"
                  [migrationMode]="true"
                  [hideHeader]="true"
                  [sourceStackSetTemplateUrl]="sourceStackSetTemplateUrl"
                  [targetStackSetTemplateUrl]="targetStackSetTemplateUrl"
                  [sourceTemplateBucket]="sourceTemplateBucket"
                  [sourceTemplateKey]="sourceTemplateKey"
                  [targetTemplateBucket]="targetTemplateBucket"
                  [targetTemplateKey]="targetTemplateKey"
                  [sourceAccountId]="sourceAccountId"
                  [sourceRegion]="sourceRegion"
                  [targetAccountId]="targetAccountId"
                  [targetRegion]="targetRegion"
                  [sourceStackName]="sourceAccessService?.stack"
                  [targetStackName]="targetAccessService?.stack"
                  [sourceManagedByStackSet]="sourceManagedByStackSet"
                  [targetManagedByStackSet]="targetManagedByStackSet"
                  [sourceAccessState]="sourceAccessService?.state"
                  [targetAccessState]="targetAccessService?.state"
                  [stickyReferenceId]="'application-editor-tabs'"
                  [testingSource]="isTestingSourceAccess"
                  [testingTarget]="isTestingTargetAccess"
                  [sourceError]="sourceAccessService?.hasApiError"
                  [targetError]="targetAccessService?.hasApiError"
                  [sourceTestCount]="sourceAccessService?.tests"
                  [targetTestCount]="targetAccessService?.tests"
                  [arpioAccountId]="accountId"
                  [regenerationJobId]="regenerationJobId"
                  (testAccess)="onTestAccess($event)"
                  (finishSetup)="onFinishStackSetsSetup()"
                ></stacksets-instructions>
              }
              <!-- Update mode: Show simpler update instructions -->
              @if (!needsStackSetsSetup) {
                <stacksets-update-instructions
                  class="margin-top"
                  [sourceStackSetTemplateUrl]="sourceStackSetTemplateUrl"
                  [targetStackSetTemplateUrl]="targetStackSetTemplateUrl"
                  [sourceAccountId]="sourceAccountId"
                  [sourceRegion]="sourceRegion"
                  [targetAccountId]="targetAccountId"
                  [targetRegion]="targetRegion"
                  [sourceStackName]="sourceAccessService?.stack"
                  [targetStackName]="targetAccessService?.stack"
                  [sourceManagedByStackSet]="sourceManagedByStackSet"
                  [targetManagedByStackSet]="targetManagedByStackSet"
                  [sourceAccessState]="sourceAccessService?.state"
                  [targetAccessState]="targetAccessService?.state"
                  [sourceTestCount]="sourceAccessService?.tests"
                  [targetTestCount]="targetAccessService?.tests"
                  [arpioAccountId]="accountId"
                  [regenerationJobId]="regenerationJobId"
                  (testAccess)="onTestAccess($event)"
                ></stacksets-update-instructions>
              }
            }
          }
          @if (applicationType === 'azure') {
            <div class="upgrade-section">
              <h2>Update Access Templates</h2>
              <p class="intro">
                We've enhanced {{ uiService.productName }}. To get this new functionality, update the Deployment Stacks
                that grant {{ uiService.productName }} access to your Azure environment.
              </p>
            </div>
            <azure-access-view
              class="margin-top"
              [state]="targetAccessService?.state"
              [accessType]="AwsAccessScope.TARGET"
              [installCommand]="targetInstallCommand"
              [subscriptionId]="targetAccountId"
              [region]="targetRegion"
              [completedTestCount]="targetAccessService?.tests"
              (buttonClicked)="targetAccessService?.validateAccess()"
            >
            </azure-access-view>
            <azure-access-view
              class="margin-top"
              [state]="sourceAccessService?.state"
              [accessType]="AwsAccessScope.SOURCE"
              [installCommand]="sourceInstallCommand"
              [subscriptionId]="sourceAccountId"
              [region]="sourceRegion"
              [completedTestCount]="sourceAccessService?.tests"
              (buttonClicked)="sourceAccessService?.validateAccess()"
            >
            </azure-access-view>
          }
        }
      </div>
    }
    <!-- END UPGRADE TAB CONTENT -->
  </form>
</div>

<modal-dialog #deleteApplicationDialog [dialogTemplate]="deleteApplicationDialogTemplate"></modal-dialog>
<modal-dialog #unsavedChangesDialog [dialogTemplate]="unsavedChangesDialogTemplate"></modal-dialog>

<ng-template #deleteApplicationDialogTemplate>
  <div class="flex-center-column full-width">
    <confirm-action-dialog
      name="&quot;{{ application.name }}&quot;"
      [error]="null"
      (onCancel)="dismissDeleteModal()"
      (onAction)="deleteApplication()"
      [busy]="deletingApplication"
    >
      <div class="error margin-top">
        Note: Deleting this application will destroy resources in the recovery environment that are not included in
        another application. After deletion, you will no longer be able to recover the application in the event of a
        disaster.
      </div>
    </confirm-action-dialog>
  </div>
</ng-template>

<ng-template #unsavedChangesDialogTemplate>
  <!-- Escape-to-dismiss ("Keep editing") is handled by a
          @HostListener('document:keydown.escape') in application-editor.component.ts,
          gated on unsavedChangesDialog.isOpen so it only fires for this modal. -->
  <div class="flex-left-column light-control-host">
    <!-- Standard modal header: title left, dismiss control right. Uses the
            global .modal-header / .modal-close-btn pattern (see styles.scss). -->
    <div class="modal-header">
      <h3 class="panel-title">Unsaved changes</h3>
      <button
        type="button"
        class="modal-close-btn"
        (click)="dismissUnsavedChangesDialog()"
        pTooltip="Close"
        tooltipPosition="left"
        aria-label="Close"
      >
        <svg lucideX></svg>
      </button>
    </div>
    <!-- Muted helper copy under the title — styled by .modal-subtitle. -->
    <div class="modal-subtitle">You have unsaved changes that will be lost if you leave this page.</div>

    <!-- Body: grouped summary of pending edits. -->
    <div class="changes-summary">
      @for (group of pendingChanges; track group) {
        <div class="change-group">
          <div class="change-category">{{ group.category }}</div>
          <ul class="change-list">
            @for (change of group.changes; track change) {
              <li>{{ change }}</li>
            }
          </ul>
        </div>
      }
    </div>

    <!-- Footer: primary destructive action right, cancel to its left. Uses
            the shared .modal-footer pattern so every modal's footer reads the
            same app-wide. -->
    <div class="modal-footer">
      <button class="arpio-secondary-button" type="button" (click)="dismissUnsavedChangesDialog()">Keep Editing</button>
      <button class="arpio-button" type="button" (click)="confirmDiscardChanges()">Discard Changes</button>
    </div>
  </div>
</ng-template>
